import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";
import { EntityStore } from "@use/entity-store";

import { urls } from "@courses-pm2/api/src/ts/config";
import { parseSimpleHtml } from "@courses-pm2/api/src/ts/parser/parseSimpleHtml";
import { Engine, getRequestHeaders } from "@courses-pm2/api/src/ts/utils";

/**
 * Internal cache that'll keep element data in memory for up to 30 minutes to
 * prevent fetching the same data over and over again.
 */
const cache = new EntityStore<React.ReactNode>({ tti: 1800 });

/**
 * Specification of the response from the word definition endpoint.
 */
type WordDefinitionResponse = {
    /**
     * The definition will usually be given under this name...
     */
    definition?: string;

    /**
     * ... But for some reason, the word definition might be exposed under this
     * name.
     */
    e?: string;
};

/**
 * Helper that'll reach out to the remote backend, and fetch the defintion of a
 * glossary word.
 */
export async function getWordDefinition<T extends {} = {}>({ user, portalId, wordId, previewMode, engine }: {
    portalId: string;
    wordId: string;
    user?: AuthenticatedUserModel;
    previewMode?: boolean;
    engine?: Engine<T>;
}): Promise<React.ReactNode> {
    const cacheId = `${portalId}-${wordId}-${user ? user.id : "guest"}`;

    if (!cache.has(cacheId)) {
        // Fetch the content for the given element
        const response = await axios.get<WordDefinitionResponse>(
            gGateway(`${urls.pm2}/v1/portals/${portalId}/glossaryterms/${wordId}`),
            {
                headers: await getRequestHeaders({ user, portalId, previewMode }),
            },
        );

        // Finally cache the fetched data
        cache.set(cacheId, parseSimpleHtml(response.data.definition || response.data.e || ""));
    }

    return cache.get(cacheId);
}
